import { Helmet } from "react-helmet-async";
import {
  Brain,
  Sparkles,
  Network,
  BookMarked,
  Quote,
  Search,
  ArrowRight,
} from "lucide-react";
import Navbar from "../components/homepage/Navbar";
import Footer from "../components/homepage/Footer";
import CTABanner from "../components/homepage/CTABanner";
import Stats from "../components/homepage/Stats";
import PageHero, {
  SectionHeader,
  FeatureGrid,
  NumberedStrip,
} from "../components/shared/PageHero";

const PILLARS = [
  {
    icon: BookMarked,
    title: "Canonical Content Engineering",
    text: "Structured, fact-dense content LLMs cite — built around your app's category queries, not vanity keywords.",
  },
  {
    icon: Network,
    title: "Knowledge Graph Presence",
    text: "Schema.org entities, Wikidata/Wikipedia seeding, G2 / Product Hunt / Crunchbase alignment — so AI models know you exist.",
  },
  {
    icon: Quote,
    title: "Citation-Worthy Assets",
    text: "Research reports, comparison pages, founder interviews — the shape of content LLMs prefer to quote.",
  },
  {
    icon: Search,
    title: "LLM Answer Monitoring",
    text: "We track mentions across ChatGPT, Claude, Gemini, Perplexity and Copilot — with a weekly share-of-voice report.",
  },
  {
    icon: Sparkles,
    title: "Prompt Surface Optimization",
    text: "Engineer presence in the 50 prompts users actually ask about your category — not the ones you wish they asked.",
  },
  {
    icon: Brain,
    title: "Model-Specific Tuning",
    text: "Different LLMs cite differently. We tune per model (GPT-5.x, Claude 4.5, Gemini 3, Perplexity Sonar).",
  },
];

const PROCESS = [
  { title: "Prompt Map", text: "Identify the 50–200 queries users ask LLMs about your category." },
  { title: "Baseline Scan", text: "Measure your current citation share across every major model + market." },
  { title: "Asset Build", text: "Engineer citation-worthy content: comparisons, rankings, canonical answers." },
  { title: "Seed & Signal", text: "Plant entity signals across the web sources LLMs train on." },
  { title: "Track & Tune", text: "Weekly answer monitoring. Double down on winning prompts." },
];

const MODELS = [
  { name: "ChatGPT", engine: "OpenAI GPT-5.x", share: "47%" },
  { name: "Claude", engine: "Anthropic Sonnet 4.5", share: "18%" },
  { name: "Gemini", engine: "Google Gemini 3", share: "15%" },
  { name: "Perplexity", engine: "Sonar + frontier", share: "12%" },
  { name: "Copilot", engine: "Microsoft 365", share: "8%" },
];

export default function GeoAiCitationPage() {
  return (
    <>
      <Helmet>
        <title>
          Generative Engine Optimization (GEO) — Get Cited by ChatGPT, Claude, Gemini | Lumotis
        </title>
        <meta
          name="description"
          content="Lumotis's GEO service gets your app cited and recommended inside ChatGPT, Claude, Gemini and Perplexity answers. Knowledge graph, canonical content engineering and LLM answer monitoring."
        />
        <meta
          name="keywords"
          content="generative engine optimization, GEO, LLMO, AI SEO, ChatGPT citation, Claude citation, Gemini recommendations, Perplexity ranking, AI brand citation"
        />
        <link rel="canonical" href="https://lumotis.com/geo-ai-citation" />
      </Helmet>
      <Navbar />
      <main data-testid="geo-page">
        <PageHero
          testid="geo-hero"
          eyebrow="GEO / AI Citation"
          breadcrumb={[{ label: "Home", href: "/" }, { label: "GEO / AI Citation" }]}
          title="When someone asks an AI for the best app in your category,"
          titleAccent="we make sure it's yours."
          subtitle="Generative Engine Optimization is the next ASO. We engineer your app's presence inside ChatGPT, Claude, Gemini, Perplexity and Copilot answers — across the 50 prompts your future users are already typing."
          primaryCta={{
            to: "/contact",
            label: "Scan my AI presence",
            testid: "geo-cta-primary",
          }}
          secondaryCta={{
            to: "/case-studies",
            label: "See GEO wins",
            testid: "geo-cta-secondary",
          }}
          rightSlot={
            <div className="bg-charcoal text-white p-8 shadow-2xl shadow-electric/20 rounded-2xl">
              <div className="flex items-center justify-between mb-5">
                <p className="text-overline text-electric">Live LLM mentions</p>
                <span className="w-2 h-2 bg-electric animate-blink" />
              </div>
              <div className="space-y-4">
                {[
                  { model: "ChatGPT", before: 2, after: 11 },
                  { model: "Claude", before: 0, after: 7 },
                  { model: "Gemini", before: 1, after: 5 },
                  { model: "Perplexity", before: 3, after: 14 },
                ].map((r) => (
                  <div
                    key={r.model}
                    className="flex items-center justify-between border-b border-white/10 pb-3 last:border-0"
                  >
                    <div className="font-display font-bold">{r.model}</div>
                    <div className="flex items-center gap-3">
                      <span className="text-zinc-500 line-through text-sm">
                        {r.before}
                      </span>
                      <ArrowRight className="w-4 h-4 text-electric" />
                      <span className="font-display text-2xl font-black text-electric">
                        {r.after}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
              <p className="mt-4 text-xs text-zinc-400">
                Citations / 100 category prompts · 90-day uplift
              </p>
            </div>
          }
        />

        <section className="py-20 md:py-28 bg-zinc-50 border-y border-zinc-200">
          <div className="container">
            <SectionHeader
              eyebrow="01 — Why GEO now"
              title="AI is becoming the new App Store search bar."
              description="65% of Gen Z users now ask an LLM for app recommendations before opening the store."
            />
            <div className="grid grid-cols-2 md:grid-cols-5 divide-x divide-zinc-200 border-y border-zinc-200">
              {MODELS.map((m, idx) => (
                <div
                  key={m.name}
                  className="px-5 py-10 first:pl-0"
                  data-testid={`geo-model-${idx}`}
                >
                  <p className="text-overline text-zinc-500 mb-3">{m.engine}</p>
                  <p className="font-display text-3xl md:text-4xl font-black text-charcoal leading-none">
                    {m.name}
                  </p>
                  <p className="mt-3 text-sm text-electric font-semibold">
                    {m.share} share
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-20 md:py-28 bg-white">
          <div className="container">
            <SectionHeader
              eyebrow="02 — Pillars"
              title="Six disciplines of Generative Engine Optimization."
              description="GEO isn't SEO with a new hat. It's a fundamentally different optimization loop."
            />
            <FeatureGrid items={PILLARS} testidPrefix="geo-pillar" />
          </div>
        </section>

        <section className="py-20 md:py-28 bg-charcoal text-white relative overflow-hidden">
          <div className="absolute inset-0 bg-grid-pattern-dark opacity-50 pointer-events-none" />
          <div className="container relative">
            <div className="max-w-3xl mb-14">
              <p className="text-overline text-electric mb-3">03 — Process</p>
              <h2 className="font-display text-4xl md:text-5xl font-extrabold text-balance">
                90 days to show up inside the AI answer.
              </h2>
            </div>
            <NumberedStrip steps={PROCESS} testidPrefix="geo-step" />
          </div>
        </section>

        <Stats />
        <CTABanner />
      </main>
      <Footer />
    </>
  );
}
